import React, { useMemo, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import apiCall from "store/services/sp.services";
import silApi from "store/services/sil.services";
import {
  categoryAppliesToFormModule,
  categoryNamesMatch,
  extractCreatedFormCategory,
  mapCategoryOption,
  normalizeCategoryName,
  orderFormCategoriesCustomAfterOther,
  splitFormCategories,
} from "./formCategoryUtils";
import "components/common/Select/Select.css";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "42px",
    borderColor: state.isFocused ? "#4d97df" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #4d97df" : "none",
    "&:hover": {
      borderColor: "#4d97df",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
};

const FormCategorySelect = ({
  categories = [],
  selectedOption,
  onChange,
  onCategoryCreated,
  role = "crm",
  isDisabled = false,
  menuInModal = false,
}) => {
  const [isCreating, setIsCreating] = useState(false);

  const orderedCategories = useMemo(
    () => orderFormCategoriesCustomAfterOther(categories),
    [categories]
  );

  const flatOptions = useMemo(
    () => orderedCategories.map((category) => mapCategoryOption(category)),
    [orderedCategories]
  );

  const options = useMemo(() => {
    const { platform, custom } = splitFormCategories(orderedCategories);
    const groups = [];

    if (platform.length > 0) {
      groups.push({
        label: "Platform categories",
        options: platform.map((category) => mapCategoryOption(category)),
      });
    }

    if (custom.length > 0) {
      return flatOptions;
    }

    return groups.length > 0 ? groups : flatOptions;
  }, [orderedCategories, flatOptions]);

  const handleCreateOption = async (inputValue) => {
    const name = normalizeCategoryName(inputValue);
    if (!name || name.length < 2) {
      toast.error("Category name must be at least 2 characters.");
      return;
    }

    const moduleFor = role === "hrm" ? "hrm" : role === "sil" ? "sil" : "crm";
    const existing = flatOptions.find(
      (option) =>
        categoryNamesMatch(option.label, name) &&
        categoryAppliesToFormModule(option, moduleFor)
    );
    if (existing) {
      onChange(existing);
      return;
    }

    setIsCreating(true);
    try {
      const response =
        role === "sil"
          ? await silApi.silCreateFormCategoryApi({ name })
          : await apiCall.createFormCategoryRequest({
              name,
              for: moduleFor,
            });
      const created = extractCreatedFormCategory(response);
      if (!created?.value) {
        throw new Error("Unable to create category.");
      }

      onCategoryCreated?.(created);
      onChange(created);
      toast.success("Category created.");
    } catch (error) {
      toast.error(error?.message || "Unable to create category.");
    } finally {
      setIsCreating(false);
    }
  };

  const mergedStyles = {
    ...selectStyles,
    ...(menuInModal
      ? {
          menuPortal: (base) => ({ ...base, zIndex: 100001 }),
        }
      : {}),
  };

  return (
    <CreatableSelect
      className='basic-single-select'
      classNamePrefix='select'
      isClearable={false}
      isDisabled={isDisabled || isCreating}
      isLoading={isCreating}
      options={options}
      value={selectedOption}
      onChange={onChange}
      onCreateOption={handleCreateOption}
      placeholder='Select or type to create a category'
      formatCreateLabel={(inputValue) => `Create category "${inputValue.trim()}"`}
      styles={mergedStyles}
      menuPortalTarget={menuInModal ? document.body : undefined}
      menuPosition={menuInModal ? "fixed" : undefined}
      noOptionsMessage={() => "Type to create a new category"}
    />
  );
};

export default FormCategorySelect;
